'use client';

import type { MouseEvent, ReactNode } from 'react';
import {
  ProductAddToCart,
  ProductAddToCartLabel,
  ProductAddToCartVariant,
} from '@vs-shop/commerce-ui/productAddToCart';
import {
  ProductBundleSummaryLines,
  ProductBundleSummaryNote,
  ProductBundleSummaryStack,
  ProductBundleSummaryTotal,
} from '@vs-shop/commerce-ui/productBundle';
import { shouldAddToCartButtonBeDisabled } from '../../../../shared/utils/stockValidation';
import { Array } from 'effect';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAddToCart, useVariantInCartLookup } from '../../../../hook/useAddToCart';
import { useCurrency } from '../../../../hook/useCurrency';
import { useCartLinePreviewPreload, withCartLinePreview } from '../../../../hook/cartLinePreview';
import type { ProductAccessoryFields } from '../../../../product/productAccessoryFields';

import { IconCartOutline } from '@vs-shop/ui/icons';
import { IntlMoney } from '../../../server/intl/Intl';

type Props = {
  selectedCount: number;
  totalOriginal: number;
  selectedItems: readonly ProductAccessoryFields[];
  // totalFinal: number;
  // discount: number;
};

const renderProductCountBold = (chunks: ReactNode) => (
  <span key="b" className="font-bold">
    {chunks}
  </span>
);

export default function ProductDiscountSetTotal({
  selectedCount,
  totalOriginal,
  selectedItems,
  // totalFinal,
  // discount,
}: Props) {
  const intl = useIntl();
  const currency = useCurrency();

  const addToCart = useAddToCart();
  const variantInCart = useVariantInCartLookup();

  const cartRequests = selectedItems.map((item) =>
    withCartLinePreview(
      { product: item, quantity: item.quantity },
      item.imagePath,
      item.ageRestriction
    )
  );
  const preloadHandlers = useCartLinePreviewPreload(cartRequests.map((request) => request.preview));

  const itemsWithCartQuantity = selectedItems.map((item) => ({
    ...item,
    ...variantInCart(item.code),
  }));
  const isAdding = itemsWithCartQuantity.some((item) => item.isAdding);

  const onAddProductsToCart = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (Array.isReadonlyArrayNonEmpty(cartRequests)) {
      addToCart(cartRequests);
    }
  };

  const isDisabled =
    isAdding ||
    selectedItems.length === 0 ||
    itemsWithCartQuantity.some((item) =>
      shouldAddToCartButtonBeDisabled(
        item.stock.eshop.maxOrderable,
        item.quantityInCart,
        item.quantity,
        item.price
      )
    );

  return (
    <ProductBundleSummaryStack>
      <ProductBundleSummaryLines>
        {/* TODO:613: add discount when BE ready for discounts (this is not ez :((( )*/}
        <ProductBundleSummaryNote>
          <FormattedMessage
            id="component.client.page.product.productSetTotal.title"
            defaultMessage="Celkem <b>{count} {count, plural, one {produkt} other {produkty}}</b> za"
            values={{
              count: selectedCount,
              b: renderProductCountBold,
            }}
          />
        </ProductBundleSummaryNote>
        <ProductBundleSummaryTotal>
          <IntlMoney value={{ amount: totalOriginal, currency: currency }} />
        </ProductBundleSummaryTotal>
      </ProductBundleSummaryLines>

      <ProductAddToCart
        variant={ProductAddToCartVariant.SLIM}
        loading={isAdding}
        disabled={isDisabled}
        {...preloadHandlers}
        onClick={onAddProductsToCart}
      >
        <IconCartOutline className="size-4 shrink-0 @sm:size-5" />
        <ProductAddToCartLabel variant={ProductAddToCartVariant.SLIM}>
          {intl.formatMessage({
            id: 'component.client.page.product.productDiscountSetTotal.addToCart',
            defaultMessage: 'Do košíku',
          })}
        </ProductAddToCartLabel>
      </ProductAddToCart>
    </ProductBundleSummaryStack>
  );
}
